import { type ReactNode } from "react";
import useAsync from "react-use/esm/useAsync";

import { parseEntityRef, stringifyEntityRef } from "@backstage/catalog-model";
import { identityApiRef, useApi } from "@backstage/core-plugin-api";

import { useTemplateEntity } from "../templateEntity";
import { ExperimentalTemplateNotice } from "./ExperimentalTemplateNotice";

const EXPERIMENTAL_LIFECYCLE = "experimental";

type Props = { children: ReactNode };

/**
 * Guards the scaffolder template page by intercepting experimental templates.
 *
 * Reads the template entity from the surrounding `TemplateEntityProvider` and,
 * only for experimental templates, the current user's identity. When the
 * entity's lifecycle is `"experimental"` and the user is not in the
 * template's `spec.owner` group, the normal page content is replaced with an
 * {@link ExperimentalTemplateNotice}. While loading, when there is no
 * template entity, or when the identity can't be read, children are rendered
 * unchanged.
 *
 * @param props - {@link Props}
 */
export function ExperimentalGuard({ children }: Props) {
    const { entity, loading } = useTemplateEntity();
    const identityApi = useApi(identityApiRef);
    const isExperimental = entity?.spec.lifecycle === EXPERIMENTAL_LIFECYCLE;

    const { value: identity, loading: identityLoading } = useAsync(
        async () =>
            isExperimental ? identityApi.getBackstageIdentity() : undefined,
        [isExperimental]
    );

    if (
        loading ||
        identityLoading ||
        entity?.spec.lifecycle !== EXPERIMENTAL_LIFECYCLE ||
        !identity
    ) {
        return <>{children}</>;
    }

    const { owner } = entity.spec;

    if (!owner) {
        return <>{children}</>;
    }

    const ownerRef = stringifyEntityRef(
        parseEntityRef(owner, {
            defaultKind: "Group",
            defaultNamespace: "default",
        })
    );
    const isOwner = identity.ownershipEntityRefs.includes(ownerRef);

    if (isOwner) {
        return <>{children}</>;
    }

    return <ExperimentalTemplateNotice entity={entity} />;
}
